/**
 * The React/SSR half of the deployment check. P25009-375.
 *
 * Its only job is the `robots` meta tag: on a deployed test site every page
 * must say "noindex" in its own HTML, not just in the HTTP header the Express
 * server adds. A tester reading the page source should see the same answer the
 * crawler gets.
 *
 * Two deliberate differences from the server twin
 * (server/api-util/environment.js), which is the authoritative gate:
 *
 *   - An unknown hostname reads as production here. This function only *adds* a
 *     meta tag, while the server's `X-Robots-Tag` is deny-by-default, so a
 *     misconfigured deployment is still protected where it counts.
 *   - Local development hosts read as production. No crawler can reach
 *     localhost, so the tag would say nothing useful — and the whole test suite
 *     runs with `marketplaceRootURL: 'http://localhost:3000'`, where the pages
 *     under test should behave like the real ones. The server still sends
 *     `noindex` for localhost.
 *
 * The target of this file is therefore the case that actually leaked: the
 * public dev app on Heroku, whose pages were index/follow.
 */

/** The live site. Override with REACT_APP_PRODUCTION_HOSTNAMES if it changes. */
export const DEFAULT_PRODUCTION_HOSTNAMES = ['www.wheelplace.com'];

/** Hostnames that only exist on a developer's own machine. */
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'];

/**
 * The hostnames that count as the real site.
 * The env var is REACT_APP_-prefixed so both this browser build and the server
 * twin read one value.
 *
 * @return {String[]} lowercase hostnames
 */
export const productionHostnames = () => {
  const configured = (process.env.REACT_APP_PRODUCTION_HOSTNAMES || '')
    .split(',')
    .map(h => h.trim().toLowerCase())
    .filter(Boolean);
  return configured.length > 0 ? configured : DEFAULT_PRODUCTION_HOSTNAMES;
};

/**
 * The hostname part of a URL, or '' when it cannot be read.
 *
 * @param {String} url
 * @return {String} lowercase hostname
 */
export const hostnameOf = url => {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch (e) {
    return '';
  }
};

/** True for localhost and friends, including a `*.local` machine name. */
export const isLocalHostname = hostname =>
  LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith('.local');

/**
 * True only for a deployed site that is not the live one — the dev app, a
 * review app. See the note at the top of this file for the two cases that
 * deliberately read as false.
 *
 * @param {String} rootURL the marketplace root URL, i.e. config.marketplaceRootURL
 * @return {Boolean}
 */
export const isNonProductionEnvironment = rootURL => {
  const hostname = hostnameOf(rootURL);
  if (!hostname || isLocalHostname(hostname)) {
    return false;
  }
  return !productionHostnames().includes(hostname);
};
